/**
 * https://wiki.ncsoft.com/display/PROD/Platform-Login-Captcha
 * 의 내용을 팀내에서 모듈 형식으로 변경한 파일 입니다.
 * => 변경한 파일을 다시 typescript를 적용한 파일입니다
 */

import axios from 'axios';
export type Env = 'rc-tw' | 'rc' | 'sb' | 'sandbox' | 'live' | 'tw' | 'op' | 'dev';
type CatpchaType = 'google' | 'wave';

type CaptchaObj = {
  showCaptcha: () => void;
  refresh: () => void;
  execute: (() => Promise<CaptchaData>) | (() => '');
  captchaType: CatpchaType;
} & Record<string, any>;

type CaptchaData = {
  type: CatpchaType;
  response: string;
  clientKey: string;
};

function getCaptchaSiteUrl(env: Env) {
  if (env === undefined) {
    env = 'live';
  }

  let url;

  switch (env) {
    case 'tw':
      url = 'https://tw.ncsoft.com';
      break;
    case 'rc-tw':
      url = 'https://rc-tw.ncsoft.com';
      break;
    case 'op':
      url = 'https://op-captcha.plaync.com';
      break;
    case 'dev':
      url = 'http://dev.captcha.plaync.com';
      break;
    case 'rc':
      url = 'https://rc-captcha.plaync.com';
      break;
    case 'sandbox':
    case 'sb':
      url = 'https://sb-captcha.plaync.com';
      break;
    default:
      url = 'https://captcha.plaync.com';
  }
  return url;
}

const googleService = function (
  captchaTagId: string,
  captchaType: 'google',
  captchaKey: string
): CaptchaObj {
  let obj: CaptchaObj;

  // let captchaExcuteEvent =;

  return (obj = {
    showCaptcha: async function (): Promise<boolean> {
      return new Promise((res) => {
        // load google recaptcha js
        const googleJS = document.createElement('script');
        googleJS.type = 'text/javascript';
        googleJS.src =
          'https://www.google.com/recaptcha/api.js?render=explicit&onload=grecaptchaOnload';
        // googleJS.onload = obj._render;
        window.grecaptchaOnload = obj._render;
        document.body.appendChild(googleJS);
        document.getElementById(captchaTagId)!.addEventListener('grecaptcha:onload', ((
          evt: CustomEvent<boolean>
        ) => {
          res(evt.detail); // boolean
        }) as EventListener);
      });
    },

    _render: function () {
      window.grecaptcha.render(captchaTagId, {
        sitekey: captchaKey,
        // sitekey: '6LcGddgaAAAAAEra4c4WUAe1M-TUoiJgGUHsrdPr',
        callback: obj._onCallback,
        size: 'invisible',
        badge: 'bottomleft',
        // 'expired-callback': '', // 없어도 잘동작함
        'error-callback': obj._onErrorCallback,
      });

      window.grecaptcha.ready(() => {
        document
          .getElementById(captchaTagId)
          ?.dispatchEvent(new CustomEvent('grecaptcha:onload', { bubbles: true, detail: true }));
      });
    },

    _onErrorCallback: function () {
      // 캡챠 로딩이 연속적으로 실패 할 경우
      if (obj._errorCount!++ > 5) {
        obj.execute = () => ''; // 캡챠가 안붙으면 execute시 '' 생성 (프론트 애러 방지?)
        console.error('캡챠 로딩 실패');
      } else {
        setTimeout(window.grecaptcha.reset, 500);
      }
    },
    _onCallback: function (token: string) {
      document
        .getElementById(captchaTagId)
        ?.dispatchEvent(new CustomEvent('grecaptcha:execute', { bubbles: true, detail: token }));
    },
    refresh: function () {
      window.grecaptcha.reset();
    },
    execute: function () {
      return new Promise((res, rej) => {
        // v2 에서는 execute를 연속으로 실행시 render시 설정해준 callback이 동작하지 않는다. - https://stackoverflow.com/questions/66061518/google-recaptcha-v2-invisible-only-fires-onces
        // 따라서 getResponse() 먼저 체크 함
        let token: string = window.grecaptcha.getResponse();
        if (token) res(obj._getCaptchaData(token));

        // 아직 excute 되지 않아서 response가 없다면
        window.grecaptcha.execute(); // 성공하면 render에서 설정해준 callback 실행,

        document.getElementById(captchaTagId)!.addEventListener('grecaptcha:execute', ((
          evt: CustomEvent<string>
        ) => {
          res(obj._getCaptchaData(evt.detail)); // token
        }) as EventListener);
      });
    },
    _getCaptchaData: function (token: string) {
      return {
        type: captchaType,
        response: token,
        clientKey: captchaKey,
      };
    },
    captchaType,
    _errorCount: 0,
  });
};

const waveService = function (
  captchaTagId: string,
  captchaType: 'wave',
  captchaKey: string,
  captchaUrl: string,
  option: { env: Env; targetId: string }
): CaptchaObj {
  let obj: CaptchaObj;

  return (obj = {
    showCaptcha: function () {
      obj.injectWaveCaptchaCSS();
      obj.injectWaveCaptchaHtml();
    },
    _getCaptchaData: function (inputText: string) {
      return {
        type: captchaType,
        response: inputText,
        clientKey: captchaKey,
      };
    },

    // 정확한 의미로는 invisible 형태 외에
    // 1) checkbox 형태의 recaptcha또는
    // 2) 유저가 텍스트를 입력하는 wave 캡차는
    // 캡챠를 프로그래밍적으로 실행할 필요가 없다.
    // 하지만 recaptcha의 경우 checkbox 는 사용하지 않는게 현재의 내부 방침이며
    // wave 캡챠의 경우 메서드 통일로 사용자가 사용하기 쉽게(구글일때만 execute를 실행하기 위해 분기 태우는 행위 x) 하기 위해 만듬
    // 즉, recaptcha invisible 의 사용법과 같이
    // 서버에 보낼대 excute 실행후, 유저가 입력한 값을 가져 온다.
    execute: function () {
      return new Promise((res, rej) => {
        const inputText = (document.getElementById('capt') as HTMLInputElement).value;

        res(obj._getCaptchaData(inputText));
      });
    },
    refresh: function () {
      captchaInit(option);
      obj.injectWaveCaptchaCSS();
      obj.injectWaveCaptchaHtml();
    },
    injectWaveCaptchaCSS: function () {
      const style = document.createElement('link');
      style.rel = 'stylesheet';
      style.type = 'text/css';
      style.media = 'screen';
      style.href = 'https://wstatic-cdn.plaync.com/gnb/openapi/wavecaptcha.css?v=201901311434';
      document.getElementsByTagName('head')[0].appendChild(style);
    },
    injectWaveCaptchaHtml: function () {
      const captchaImageUrl =
        captchaUrl + '/captcha/image?type=' + captchaType + '&clientKey=' + captchaKey;
      const injectHtml =
        "<div class='input-captcha text-captcha'>" +
        "<div class='wrap-text-captcha'>" +
        "<div class='captcha-img'><img src=" +
        captchaImageUrl +
        " id='captcha_img' alt='' /></div>" +
        "<div class='captcha-text'>" +
        "<div class='input-text'><input type='text' id='capt' name='capt' autocomplete='off' placeholder='請輸入驗證碼' /></div>" +
        "<a id='reset_captcha' class='reload' href='javascript:(function(){ var d = new Date(); document.getElementById(\"waveCaptchaRetryUrl\").innerText = \"" +
        captchaImageUrl +
        '" + "&v=" + d.getTime(); document.getElementById("captcha_img").setAttribute("src", document.getElementById("waveCaptchaRetryUrl").innerText);})()\'>刷新驗證碼</a>' +
        '</div>' +
        '</div>' +
        // "<span class='msg'></span>" +
        '</div>' +
        "<div id='waveCaptchaKey' style='display:none'></div>" +
        "<div id='waveCaptchaRetryUrl' style='display:none'></div>";

      const captchaDiv = document.getElementById(captchaTagId) as HTMLElement;
      captchaDiv.innerHTML = injectHtml;
      document.getElementById('waveCaptchaKey')!.innerText = captchaKey;
    },
    captchaType,
  });
};

export const captchaInit = async function (option: { env: Env; targetId: string }) {
  const url = getCaptchaSiteUrl(option.env);
  const targetId = option.targetId;

  if (targetId == null || targetId === '') {
    window.alert('캡차 초기화 실패');
    console.log('empty required parameters');
    return null;
  }

  try {
    const result: {
      type: string;
      clientKey: string;
    } & Record<string, any> = (await axios.get(url + '/captcha/info')).data;

    let g: CaptchaObj;
    if (result.type == 'google') {
      g = googleService(targetId, result.type, result.clientKey);
    } else if (result.type == 'wave') {
      g = waveService(targetId, result.type, result.clientKey, url, option);
    } else {
      console.error('Captcha Error: ');
      return null;
    }

    return g;
  } catch (err) {
    console.error('Captcha Error: ' + err);
    return null;
  }
};
